import { cn } from '@repo/ui/utils';
import { FormInputField } from '@src/components/custom/FormInputField';
import { useCallback, useState } from 'react';
import {
  BaseDialog,
  DialogClose,
  DialogFooter,
} from '@src/components/custom/BaseDialog';
import { PlusIcon } from 'lucide-react';
import { useCreateUniversity } from '@src/api/useUniversities';
import { ApiCreateUniversityRequest, ApiInstitution } from '@repo/types';
import { FetchError, getErrorMessage } from '@src/api/useApi';
import { Button } from '@repo/ui/components/Button';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { InstitutionSelector } from '@src/components/custom/InstitutionSelector';

export function AddUniversityDialog() {
  const [newAccount, setNewAccount] = useState({
    name: '',
    country: '',
    slug: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [institutionText, setInstitutionText] = useState<string>('');
  const [selectedInstitution, setSelectedInstitution] =
    useState<ApiInstitution | null>(null);
  const isMobile = useIsMobile();

  const createUniversityMutation = useCreateUniversity();

  const submitUniversity = useCallback(
    async (university: ApiCreateUniversityRequest) => {
      try {
        await createUniversityMutation.mutateAsync({
          ...university,
          institutionId: selectedInstitution
            ? selectedInstitution.id
            : undefined,
        });
        setError(null);
        setOpen(false);
      } catch (error) {
        if (error instanceof FetchError) {
          setError(getErrorMessage(error.body.message));
        } else {
          console.error(error);
          setError('An unexpected error occurred');
        }
      }
    },
    [createUniversityMutation, selectedInstitution]
  );

  const handleDialogClose = (opened: boolean) => {
    setOpen(opened);
    setNewAccount({ name: '', country: '', slug: '' });
    setSubmitted(false);
    setError(null);
  };

  return (
    <BaseDialog
      title="Create new Account"
      description="Create new account by entering their name and country."
      open={open}
      onOpenChange={handleDialogClose}
      trigger={
        <Button
          variant="ghost"
          className={cn('max-w-max')}
          aria-label="Create Account"
        >
          <PlusIcon className={cn('h-5', 'w-5', !isMobile && 'mr-2')} />
          {!isMobile && (
            <span className={cn('whitespace-nowrap')}>Create Account</span>
          )}
        </Button>
      }
    >
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          setSubmitted(true);
          submitUniversity(newAccount);
        }}
      >
        <div className={cn('flex', 'flex-col', 'gap-4')}>
          <FormInputField
            label="Name"
            id="account-name"
            required
            name="name"
            type="text"
            placeholder="Name"
            value={newAccount.name}
            onChange={(e) =>
              setNewAccount((prev) => ({ ...prev, name: e.target.value }))
            }
            error={
              submitted && !newAccount.name ? 'Please enter a name.' : null
            }
          />
          <FormInputField
            label="Country"
            id="account-country"
            required
            name="country"
            type="text"
            placeholder="Country"
            value={newAccount.country}
            onChange={(e) =>
              setNewAccount((prev) => ({ ...prev, country: e.target.value }))
            }
            error={
              submitted && !newAccount.country
                ? 'Please enter a country.'
                : null
            }
          />
          <FormInputField
            label="Slug"
            id="account-slug"
            required
            name="slug"
            type="text"
            placeholder="Slug"
            value={newAccount.slug}
            onChange={(e) =>
              setNewAccount((prev) => ({ ...prev, slug: e.target.value }))
            }
            error={
              submitted && !newAccount.slug ? 'Please enter a slug.' : null
            }
          />
          <div>
            <p className={cn('text-xs', 'mb-2')}>
              Connect the new university to an existing institution if it is not
              a child university.
            </p>
            <InstitutionSelector
              label="Institution"
              id="add-university-institution"
              onChange={(value: string) => {
                setInstitutionText(value);
                setSelectedInstitution(null);
              }}
              onInstitutionSelect={(institution) => {
                setSelectedInstitution(institution);
              }}
              value={institutionText}
              placeholder="Select institution"
            />
          </div>
        </div>
        <p
          role="alert"
          aria-atomic="true"
          className={cn(
            'mt-4',
            'text-sm',
            'text-destructive-ghost-foreground',
            !error && 'sr-only'
          )}
        >
          {error}
        </p>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="default">Cancel</Button>
          </DialogClose>
          <Button type="submit" variant="primary">
            Create Account
          </Button>
        </DialogFooter>
      </form>
    </BaseDialog>
  );
}
